import React, { useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import Colors from 'styles/export/colors.module.scss';
import { Rect, VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLabel, VictoryLegend, VictoryLine, VictoryScatter } from 'victory';
import styles from './MonthlyRouteChart.module.scss';
import kpiStyles from '../components/KpiHeader.module.scss';
import Utils from 'common/services/Utils';
import { DEFAULT_LANGUAGE, STORAGE } from 'Config';
import useElementSize from 'common/hooks/useElementSize';
import dayjs from 'dayjs';
import Storage from 'common/services/Storage';
import { MonthlyRoutesDashboardDto } from 'api/dashboard/dtos/MonthlyRouteDashboardDto';
import DistanceFormat from 'common/components/distanceFormat/DistanceFormat';
import WidgetNoData from '../components/WidgetNoData';
import openMileageList from '../utils/openMileageList';
import ChartAveragePill from '../components/ChartAveragePill';

const KM_COLOR = '#02c7c7';
const TIME_COLOR = '#038cb6';

const BarLabel = (props: any) => (
	<VictoryLabel
		{...props}
		y={props.y - 9}
		backgroundPadding={{ top: 4, left: 8, bottom: 6, right: 8 }}
		style={{ fill: Colors.white, userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' }}
		backgroundStyle={{ fill: props.fill, opacity: 1 }}
		backgroundComponent={<Rect rx={7} ry={10} />}
		verticalAnchor="middle"
		renderInPortal={false}
	/>
);

type Props = {
	data: MonthlyRoutesDashboardDto | null;
	selectedYear: number | null;
	dateFrom?: Date | null;
	dateTo?: Date | null;
};

type TooltipState = { x: number; y: number; text: string; color: string } | null;
type AvgTooltipState = { clientX: number; clientY: number; text: string; color: string } | null;

const MonthlyRouteChart = ({ data, selectedYear, dateFrom, dateTo }: Props) => {
	const { t } = useTranslation();
	const [reRenderKey, setReRenderKey] = useState(new Date().getTime());
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<TooltipState>(null);
	const [avgTooltip, setAvgTooltip] = useState<AvgTooltipState>(null);
	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;

	useEffect(() => {
		setReRenderKey(new Date().getTime());
	}, [data, selectedYear]);

	if (data && !Boolean(data.hasTrackingDeviceId)) {
		return <WidgetNoData />;
	}

	if (!data?.items || data?.items.length === 0) {
		return <WidgetNoData />;
	}

	const formatTime = (totalMinutes: number) => {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = Math.round(totalMinutes - (hours * 60));
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} h`;
	};

	const formatKm = (value: number) =>
		renderToString(<DistanceFormat value={value} fixedDecimalScale={false} />).replace(/<\/?span[^>]*>/g, '');

	const LEGEND_HEIGHT = 24;
	// Use the real container dimensions so the chart reflows properly at any widget width
	// (minW is enforced via widgetRegistry). Fall back to sensible defaults on first render.
	const chartWidth = width > 0 ? width : 650;
	const chartHeight = Math.max(120, (height > 0 ? height : 190) - LEGEND_HEIGHT);

	let itemsData = data.items;
	if (selectedYear) {
		itemsData = itemsData.filter(s => dayjs(s.date).year() === selectedYear);
	}

	// Sort by date and use index-based X axis so bars stay tight regardless of calendar gaps.
	const sortedItems = [...itemsData].sort((a, b) => a.date.getTime() - b.date.getTime());

	// Thin X-axis ticks when range is long so labels don't overlap
	const MAX_TICKS = 12;
	const TICK_STEPS = [1, 2, 3, 6, 12];
	const tickStep = TICK_STEPS.find(s => Math.ceil(sortedItems.length / s) <= MAX_TICKS) ?? 12;
	const tickIndexes = sortedItems.map((_, i) => i).filter(i => i % tickStep === 0);

	const allKms = sortedItems.map(x => x.totalKms);
	const averageKms = allKms.length ? allKms.reduce((a, b) => a + b, 0) / allKms.length : 0;
	const allMinutes = sortedItems.map(x => x.totalMinutes);
	const averageMinutes = allMinutes.length ? allMinutes.reduce((a, b) => a + b, 0) / allMinutes.length : 0;
	const maxKms = Math.max(...allKms, 1);
	const maxHours = Math.max(...sortedItems.map(x => x.totalMinutes / 60), 1);
	// Scale hour values into the KM domain so both bars share the same Y axis visually,
	// but the right-side axis shows real hours.
	const scaleFactor = maxKms / maxHours;
	// Time average projected onto the KM axis (so the dashed line sits at the right
	// vertical position relative to the time bars, which are also scaled).
	const averageHoursScaled = (averageMinutes / 60) * scaleFactor;

	const monthsDiff = sortedItems.length ? Utils.getMonthsInDates(sortedItems.map(x => x.date)).length : 0;

	const chartPadding = { top: 12, left: 15, right: 15, bottom: 30 };
	const AXIS_CLEARANCE = 24;
	const n = Math.max(sortedItems.length, 1);
	const availableDataWidth = Math.max(80, chartWidth - chartPadding.left - chartPadding.right);
	const slotWidth = availableDataWidth / n;
	const barWidth = Math.max(3, Math.min(slotWidth * 0.22, availableDataWidth / 5));
	const offset = barWidth + Math.max(1, barWidth * 0.12);
	const groupWidth = offset + barWidth;
	// Explicit x-domain so first/last bar are pushed inward by edgePadUnits in data space,
	// which translates to (groupWidth/2 + AXIS_CLEARANCE) px from the chart edge.
	const requiredEdgePx = groupWidth / 2 + AXIS_CLEARANCE;
	const dataPxPerUnit = n > 1 ? availableDataWidth / (n - 1) : availableDataWidth;
	const edgePadUnits = requiredEdgePx / dataPxPerUnit;
	const xDomain: [number, number] = [0 - edgePadUnits, (n - 1) + edgePadUnits];

	const legendData = [
		{
			name: t('dashboard.routes.AVERAGE'),
			symbol: { fill: Colors.black, type: 'minus' },
			labels: { fill: Colors.black },
		},
		{
			name: t('dashboard.routes.KM'),
			symbol: { fill: KM_COLOR },
			labels: { fill: Colors.black },
		},
		{
			name: t('dashboard.routes.TIME'),
			symbol: { fill: TIME_COLOR },
			labels: { fill: Colors.black },
		},
	];

	const kmHoverText = (datum: any) =>
		t('dashboard.routes.KM_OVER' as any, { km: formatKm(datum.totalKms) });

	const timeHoverText = (datum: any) =>
		t('dashboard.routes.TIME_OVER' as any, { time: formatTime(datum.totalMinutes) });

	const openMonthMileage = (datum: any) => {
		if (!datum?.date) return;
		const monthStart = dayjs(datum.date).startOf('month').toDate();
		const monthEnd = dayjs(datum.date).endOf('month').toDate();
		openMileageList({ dateFrom: monthStart, dateTo: monthEnd });
	};

	const openRangeMileage = () => {
		openMileageList({
			dateFrom: dateFrom ?? undefined,
			dateTo: dateTo ?? undefined,
		});
	};

	const makeBarEvents = (color: string, textFn: (datum: any) => string): any => [{
		target: 'data',
		eventHandlers: {
			onMouseEnter: (_: any, props: any) => {
				if (props?.datum) {
					setTooltip({ x: props.datum.x, y: props.datum.y, text: textFn(props.datum), color });
				}
				return [];
			},
			onMouseLeave: () => {
				setTooltip(null);
				return [];
			},
			onClick: (_: any, props: any) => {
				openMonthMileage(props?.datum);
				return [];
			},
		},
	}];

	const averageKmLabel = t('dashboard.routes.AVERAGE_OVER' as any, { value: formatKm(averageKms) });
	const averageTimeLabel = t('dashboard.routes.AVERAGE_OVER' as any, { value: formatTime(averageMinutes) });

	// Dense X positions used as transparent hit-areas under the average dashed lines,
	// so hovering between bars (in the gaps) still triggers the tooltip.
	const HIT_DENSITY = 80;
	const lastIdx = Math.max(sortedItems.length - 1, 0);
	const denseAverageXs = lastIdx === 0
		? [0]
		: Array.from({ length: HIT_DENSITY }, (_, i) => (i * lastIdx) / (HIT_DENSITY - 1));

	const makeAverageEvents = (_yValue: number, label: string, color: string): any => [{
		target: 'data',
		eventHandlers: {
			onMouseEnter: (evt: any) => {
				setAvgTooltip({ clientX: evt.clientX, clientY: evt.clientY, text: label, color });
				return [];
			},
			onMouseMove: (evt: any) => {
				setAvgTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => {
				setAvgTooltip(null);
				return [];
			},
		},
	}];

	return (
		<div className={styles.container}>
			<div className={kpiStyles.kpiHeader}>
				<div
					className={`${kpiStyles.kpiTotalGroup} ${kpiStyles.kpiTotalGroupClickable}`}
					onClick={openRangeMileage}
					role="button"
					tabIndex={0}
				>
					<div className={kpiStyles.kpiMain}>
						<div className={kpiStyles.kpiValue}>
							<DistanceFormat value={data.totalKms} fixedDecimalScale={false} />
						</div>
						<div className={kpiStyles.kpiSubLabel}>{t('dashboard.routes.KM')}</div>
					</div>
				</div>
				<div className={`${kpiStyles.kpiTotalGroup} ${styles.kpiRightGroup}`}>
					<div className={kpiStyles.kpiMain}>
						<div className={kpiStyles.kpiValue}>{formatTime(data.totalMinutes)}</div>
						<div className={kpiStyles.kpiSubLabel}>{t('dashboard.routes.TIME')}</div>
					</div>
				</div>
			</div>

			<div className={styles.chartContainer} ref={containerRef}>
				{width > 0 && height > 0 && sortedItems.length > 0 && (
					<svg width={chartWidth} height={chartHeight + LEGEND_HEIGHT} style={{ display: 'block' }}>
					<VictoryChart
						standalone={false}
						width={chartWidth}
						height={chartHeight}
						padding={chartPadding}
						domain={{ x: xDomain, y: [0, maxKms * 1.12] }}
						key={'chart-' + reRenderKey + '-' + Math.round(chartWidth)}
					>
							{/* Left Y axis: km — axisValue pins it to the left edge of the x-domain */}
							<VictoryAxis
								dependentAxis
								orientation="left"
								axisValue={xDomain[0]}
								tickCount={5}
								tickFormat={(val: number) => {
									if (val >= 1000) return `${Math.round(val / 1000)}k`;
									return String(Math.round(val));
								}}
								style={{
									axis: { stroke: Colors.gray3 },
									tickLabels: { fill: Colors.black, fontSize: 10, fontFamily: 'Roboto', textAnchor: 'end' },
									grid: { stroke: 'transparent' },
								}}
							/>
							{/* Right Y axis: hours — axisValue pins it to the right edge of the x-domain */}
							<VictoryAxis
								dependentAxis
								orientation="right"
								axisValue={xDomain[1]}
								tickCount={5}
								tickFormat={(val: number) => {
									const hrs = Math.round(val / scaleFactor);
									if (hrs >= 10000) return `${(hrs / 1000).toFixed(1)}k h`;
									return `${hrs} h`;
								}}
								style={{
									axis: { stroke: Colors.gray3 },
									tickLabels: { fill: Colors.black, fontSize: 10, fontFamily: 'Roboto', textAnchor: 'start' },
									grid: { stroke: 'transparent' },
								}}
							/>
							{/* X axis */}
							<VictoryAxis
								orientation="bottom"
								tickValues={tickIndexes}
								tickFormat={(idx: number) => {
									const date = sortedItems[idx]?.date;
									if (!date) return '';
									if (tickStep >= 6) return Utils.capitalize(dayjs(date).locale(locale).format('MMM')) + ' ' + dayjs(date).format('YY');
									if (tickStep >= 2) return Utils.capitalize(dayjs(date).locale(locale).format('MMM'));
									return Utils.capitalize(monthsDiff > 5 ? dayjs(date).locale(locale).format('MMMM')[0] : dayjs(date).locale(locale).format('MMMM'));
								}}
								style={{
									tickLabels: { fill: Colors.black, userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
									axis: { stroke: Colors.gray3 },
								}}
							/>

							{/* Grouped bars: KM + Time */}
							<VictoryGroup offset={offset}>
								<VictoryBar
									data={sortedItems.map((d, i) => ({ ...d, x: i, y: d.totalKms }))}
									alignment="middle"
									style={{ data: { fill: KM_COLOR, cursor: 'pointer' } }}
									cornerRadius={{ top: 3 }}
									barWidth={barWidth}
									events={makeBarEvents(KM_COLOR, kmHoverText)}
								/>
								<VictoryBar
									data={sortedItems.map((d, i) => ({ ...d, x: i, y: (d.totalMinutes / 60) * scaleFactor }))}
									alignment="middle"
									style={{ data: { fill: TIME_COLOR, cursor: 'pointer' } }}
									cornerRadius={{ top: 3 }}
									barWidth={barWidth}
									events={makeBarEvents(TIME_COLOR, timeHoverText)}
								/>
							</VictoryGroup>

							{/* Average KM line — teal, matches the KM bars */}
							<VictoryLine
								data={[
									{ x: 0, y: averageKms },
									{ x: sortedItems.length - 1, y: averageKms },
								]}
								style={{
									data: {
										stroke: KM_COLOR,
										strokeWidth: 1.5,
										strokeDasharray: 4,
									},
								}}
							/>
							{/* Average Time line — ocean blue, matches the Time bars */}
							<VictoryLine
								data={[
									{ x: 0, y: averageHoursScaled },
									{ x: sortedItems.length - 1, y: averageHoursScaled },
								]}
								style={{
									data: {
										stroke: TIME_COLOR,
										strokeWidth: 1.5,
										strokeDasharray: 4,
									},
								}}
							/>

							{/* Transparent hit-areas for the two average lines.
							    Densified along X so the dashed line is hoverable in the gaps between bars too. */}
							<VictoryScatter
								data={denseAverageXs.map(x => ({ x, y: averageKms }))}
								size={10}
								style={{ data: { fill: 'transparent', cursor: 'pointer' } }}
								events={makeAverageEvents(averageKms, averageKmLabel, KM_COLOR)}
							/>
							<VictoryScatter
								data={denseAverageXs.map(x => ({ x, y: averageHoursScaled }))}
								size={10}
								style={{ data: { fill: 'transparent', cursor: 'pointer' } }}
								events={makeAverageEvents(averageHoursScaled, averageTimeLabel, TIME_COLOR)}
							/>

							{/* Single tooltip layer, rendered last so it's always on top of bars and lines */}
							{tooltip && (
								<VictoryScatter
									data={[{ x: tooltip.x, y: tooltip.y, label: tooltip.text }]}
									size={1}
									style={{ data: { fill: 'transparent' } }}
									labelComponent={<BarLabel fill={tooltip.color} />}
								/>
							)}
					</VictoryChart>
						<VictoryLegend
							standalone={false}
							x={Math.max(4, chartPadding.left - 10)}
							y={chartHeight + 2}
							gutter={18}
							data={legendData}
							orientation="horizontal"
							itemsPerRow={3}
							style={{ labels: { fontSize: 11, userSelect: 'none', fontFamily: 'Roboto' } }}
						/>
					</svg>
				)}
			</div>

			{avgTooltip && (
				<ChartAveragePill clientX={avgTooltip.clientX} clientY={avgTooltip.clientY} color={avgTooltip.color}>
					{avgTooltip.text}
				</ChartAveragePill>
			)}
		</div>
	);
};

export default MonthlyRouteChart;
